import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import toast from "react-hot-toast";
import PhoneInput from "react-phone-number-input";
import { isPossiblePhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input";
import { useForm, Controller } from "react-hook-form";
import { countries as countryData } from "utils/countries";
import { usaStates } from "utils/data";

import { StoreContext } from "context/store";

const countryOptions = countryData.map((c) => ({
  label: c.name,
  value: c.alpha2.toLocaleLowerCase(),
}));

const usaStateOptions = usaStates.map((x) => {
  return {
    label: x.abbreviation,
    value: x.abbreviation,
  };
});

const CompanyInfo = () => {
  const [phone, setPhone] = useState();
  const [countryCode, setCountryCode] = useState({});
  const { store, updateStore } = useContext(StoreContext);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    const country_code = store?.metadata?.address?.country_code;
    const province = store?.metadata?.address?.province;
    reset({
      name: store.name,
      detail: store.detail,
      business_email: store?.metadata?.business_email,
      business_phone: store?.metadata?.business_phone,
      address_1: store?.metadata?.address?.address_1,
      city: store?.metadata?.address?.city,
      province: province,
      country_code: country_code,
      postal_code: store?.metadata?.address?.postal_code,
      instagram_link: store?.metadata?.address?.instagram_link,
      company_link: store?.metadata?.address?.company_link,
    });

    // reset business phone number
    setValue("business_phone", store?.metadata?.business_phone);
    setPhone(store?.metadata?.business_phone);

    // reset country code

    const record = countryData.find((x) => x.alpha2.toLowerCase() == country_code);
    const countryOption = {
      label: record?.name,
      value: record?.alpha2.toLowerCase(),
    };
    handleChangeCountry(countryOption);

    // reset state code
    if (country_code == "us") {
      const state = usaStateOptions.find((x) => x.value.toUpperCase() == province);
      setValue("province", state);
    } else {
      setValue("province", province);
    }
  }, [store]);
  const handleChangePhoneNumber = (value) => {
    setValue("business_phone", value);
    setPhone(value);
  };

  const handleChangeCountry = (option) => {
    setCountryCode(option);

    setValue("country_code", option);
    let province = getValues("province");

    if (option.value != "us") {
      if (province && typeof province == "object") {
        setValue("province", province.value);
      }
    } else if (option.value == "us" && province) {
      const provinceOpt = usaStateOptions.find((x) => x.value == province);
      if (provinceOpt && provinceOpt.value) {
        setValue("province", provinceOpt);
      } else {
        setValue("province", undefined);
      }
    }
  };

  const handleSave = async (data) => {
    const phoneNum = formatPhoneNumberIntl(data["business_phone"]);
    if (!isPossiblePhoneNumber(phoneNum)) {
      return toast.error("Not a valid phone number!");
    }
    try {
      const payload = {
        metadata: {
          address: {
            address_1: data["address_1"],
            province:
              typeof data["province"] == "object" ? data["province"].value : data["province"],
            city: data["city"],
            postal_code: data["postal_code"],
            country_code:
              typeof data["country_code"] == "object"
                ? data["country_code"].value
                : data["country_code"],
          },
          business_phone: data["business_phone"],
          business_email: data["business_email"],
          instagram_link: data["instagram_link"],
          company_link: data["company_link"],
        },
        name: data["name"],
        detail: data["detail"],
      };
      await updateStore(payload);
      toast.success("Update company information successfully!");
    } catch (err) {
      toast.error("Update company information failed!");
    }
  };

  return (
    <Card id="company-info" sx={{ overflow: "visible" }}>
      <SuiBox p={3}>
        <SuiTypography variant="h5">Company Info</SuiTypography>
      </SuiBox>
      <SuiBox component="form" pb={3} px={3} onSubmit={handleSubmit(handleSave)}>
        <Grid container spacing={3} className="items-start">
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Business name
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Company Name"
                required
                {...register("name", { required: true, maxLength: 50 })}
              />
              {errors.name?.type === "maxLength" ? (
                <SuiTypography
                  variant="text"
                  fontWeight="regular"
                  fontSize="12px"
                  color="error"
                  className="ml-2"
                >
                  Maxmium length should not exceed 50 characters
                </SuiTypography>
              ) : null}
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Business email
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Business Email"
                {...register("business_email", { required: true })}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Phone
                </SuiTypography>
              </SuiBox>

              <PhoneInput
                label="Phone"
                className="upstyled-phone-input h-10 px-2 rounded-lg"
                placeholder="Enter phone number"
                name="business_phone"
                value={phone}
                {...register("business_phone", { required: true })}
                required
                onChange={handleChangePhoneNumber}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={12}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Short Description
                </SuiTypography>
              </SuiBox>

              <SuiInput
                placeholder="Brief descripe your capabilities/services(max 100 characters)"
                required
                {...register(`detail`, { maxLength: 100 })}
              />
              {errors.detail?.type === "maxLength" ? (
                <SuiTypography
                  variant="text"
                  fontWeight="regular"
                  fontSize="12px"
                  color="error"
                  className="ml-2"
                >
                  Maxmium length should not exceed 100 characters
                </SuiTypography>
              ) : null}
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={12}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Street address
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Street Address"
                required
                {...register("address_1", { required: true })}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  City
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="City" required {...register("city", { required: true })} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  State/Province
                </SuiTypography>
              </SuiBox>
              {countryCode && countryCode.value == "us" ? (
                <Controller
                  name="province"
                  rules={{ required: true }}
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <SuiSelect
                      ref={ref}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                      placeholder="State/Province"
                      isSearchable={true}
                      options={usaStateOptions}
                      required
                    />
                  )}
                />
              ) : (
                <SuiInput
                  {...register("province", {
                    required: true,
                  })}
                  placeholder="State/Province"
                  required
                />
              )}
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Country
                </SuiTypography>
              </SuiBox>
              <Controller
                name="country_code"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <SuiSelect
                    {...field}
                    placeholder="Country"
                    isSearchable={true}
                    onChange={handleChangeCountry}
                    options={countryOptions}
                  />
                )}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Postcode/ZIP
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Postcode/ZIP"
                required
                {...register("postal_code", { required: true })}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Instagram Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.instagram.com" {...register("instagram_link")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Company Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.yourcomapany.com" {...register("company_link")} />
            </SuiBox>
          </Grid>
        </Grid>
        <SuiBox
          pt={3}
          px={3}
          display="flex"
          justifyContent="end"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                save
              </SuiButton>
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default CompanyInfo;
